import dayjs from "dayjs";
import type { ComponentType } from "react";
import {
  Activity as ActivityIcon,
  AlertTriangle,
  Calendar as CalendarIcon,
  Check,
  Clock,
  FileText,
  Lock,
  MessageCircle,
  Power,
  RotateCw,
  User as UserIcon,
  UserCog,
  UserPlus,
} from "lucide-react";
import { mapAgentTopic, mapPipelineName } from "@/utils/valueMap";
import { cn } from "@/lib/utils";
import AudioPlayer from "@/components/AudioPlayer";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  ActivityItem,
  MessageItem,
  NoteItem,
  TimelineItem,
} from "@/components/leads/chat/ConversationTimeline";
import { noteAuthorLabel } from "@/components/leadNotes/LeadNotesSection";

type PositionRef = { id: string; displayName: string; stage: string };

const formatTime = (dateTime: string) => dayjs(dateTime).format("DD/MM/YYYY HH:mm");

/**
 * Icon per activity kind. Carries the differentiation that colour would otherwise
 * have to: the tablero panel is read-only, and DESIGN.md reserves colour for
 * action and semantic state, so a multi-hue activity palette would be decoration.
 */
export const activityIcon = (
  type: ActivityItem["activity"]["type"]
): ComponentType<{ className?: string }> => {
  switch (type) {
    case "conversationStarted":
      return MessageCircle;
    case "assignment":
    case "aiAgentAssignment":
      return UserPlus;
    case "manualReassignment":
    case "partnerCrmReassignment":
      return UserCog;
    case "appointment":
      return CalendarIcon;
    case "takenByEmployee":
      return UserIcon;
    case "agentTurnedOff":
    case "agentTurnedOn":
      return Power;
    default:
      return ActivityIcon;
  }
};

export const activityLabel = (
  activity: ActivityItem["activity"],
  positionNameById: Map<string, string>
): string => {
  switch (activity.type) {
    case "conversationStarted":
      return "Conversación iniciada";
    case "assignment":
      // Prefer the headline frozen at decision time; it names the rule that applied.
      return (
        activity.assignment?.rationaleText?.split("\n\n")[0] ??
        `Asignado a ${activity.assignment?.employeeName ?? "asesor"}`
      );
    case "pipelinePositionChanged": {
      const id = activity.pipelinePositionChanged?.pipelinePosition ?? "";
      const by = activity.pipelinePositionChanged?.changedBy;
      const base = `Movido a ${positionNameById.get(id) ?? id}`;
      return by ? `${base} · por ${by}` : base;
    }
    case "appointment":
      return `Cita: ${activity.appointment?.date ?? ""} ${activity.appointment?.time ?? ""} · ${activity.appointment?.employeeName ?? ""}`;
    case "takenByEmployee":
      return `${activity.takenByEmployee?.employeeName ?? "Asesor"} tomó la conversación`;
    case "agentTurnedOff":
      return "IA desactivada";
    case "agentTurnedOn":
      return "IA activada";
    case "aiAgentAssignment": {
      const pipelineId = activity.aiAgentAssignment?.pipelineId;
      const topic = activity.aiAgentAssignment?.topic ?? "";
      const label =
        (pipelineId && mapPipelineName[pipelineId as keyof typeof mapPipelineName]) ??
        mapAgentTopic[topic as keyof typeof mapAgentTopic] ??
        pipelineId ??
        topic;
      return `Agente IA asignado: ${label}`;
    }
    case "manualReassignment": {
      const newName = activity.manualReassignment?.newEmployeeName ?? "asesor";
      const previousName = activity.manualReassignment?.previousEmployeeName;
      const by = activity.manualReassignment?.reassignedBy;
      const base = previousName
        ? `Reasignación manual: ${previousName} → ${newName}`
        : `Reasignación manual a ${newName}`;
      return by ? `${base} · por ${by}` : base;
    }
    case "partnerCrmReassignment":
      return `Reasignado a ${activity.partnerCrmReassignment?.employeeName ?? "asesor"}`;
    default:
      return "Actividad";
  }
};

const MessageStatus = ({
  message,
  onRetry,
}: {
  message: MessageItem["message"];
  onRetry?: () => void;
}) => {
  const isOutbound =
    message.source === "ai_agent" ||
    message.source === "human_agent" ||
    message.source === "merchant_api";
  if (!message.lastStatus || !isOutbound) return null;

  const status = message.lastStatus.toLowerCase();

  if (status === "pending") return <Clock className="ml-1 inline size-3 text-gray-400" />;

  if (status === "failed") {
    // The raw Meta code is shown inline because merchants quote it — to us, and to Meta
    // support. The catalog's Spanish sentence is the hover, not the label: it is a full
    // sentence and would break the timestamp line it sits on.
    const marker = (
      <span className="inline-flex items-center gap-1 text-red-500">
        <AlertTriangle className="size-3" />
        {message.failureCode != null && (
          <span className="font-semibold tabular-nums">{message.failureCode}</span>
        )}
      </span>
    );

    return (
      <span className="ml-1 inline-flex items-center gap-1">
        {message.failureReason ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">{marker}</span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-64 text-xs leading-relaxed">
                {message.failureReason}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          marker
        )}
        {onRetry && (
          <button
            aria-label="Reintentar envío"
            onClick={onRetry}
            className="inline-flex min-w-0 items-center justify-center p-1 text-red-500 hover:text-red-700"
          >
            <RotateCw className="size-3" />
          </button>
        )}
      </span>
    );
  }

  if (status === "sent") return <Check className="ml-1 inline size-3 text-gray-400" />;

  if (status === "delivered") {
    return (
      <span className="ml-1 inline-flex -space-x-1">
        <Check className="size-3 text-gray-400" />
        <Check className="size-3 text-gray-400" />
      </span>
    );
  }

  if (status === "read") {
    return (
      <span className="ml-1 inline-flex -space-x-1">
        <Check className="size-3 text-blue-500" />
        <Check className="size-3 text-blue-500" />
      </span>
    );
  }

  return null;
};

/**
 * Message bubbles keep the LEGACY conversation colour code — the one advisors were
 * trained on in the old inbox and still see in the /activities panels
 * (MessageChatInterface): blue = the customer, green = the AI, grey = an advisor,
 * orange = arrived on a second WhatsApp line, red = failed to send. The layout is
 * this panel's own; only the hues are inherited, deliberately, so the same message
 * means the same thing on every surface. Do not "modernise" these to design tokens
 * without agreeing the recode across all three surfaces first.
 */
const Burbuja = ({ item, onRetry }: { item: MessageItem; onRetry?: () => void }) => {
  const { message } = item;
  const isAI = message.source === "ai_agent";
  const isHumanAgent = message.source === "human_agent";
  // API-originated sends (e.g. an OTP via POST /v2/template/auth/send) are outbound,
  // not inbound — our side of the conversation, though neither AI nor human advisor.
  const isMerchantApi = message.source === "merchant_api";
  const isIn = !isAI && !isHumanAgent && !isMerchantApi;
  // `isPrimaryConnection` is written only on inbound rows (logic/whatsapp/inboundMessage.ts);
  // every outbound row leaves it undefined, so the second-line signal is read on inbound
  // and matched explicitly against false rather than inferred from a falsy value.
  const isSecondLine = isIn && !!message.toPhoneNumber && message.isPrimaryConnection === false;
  // Which of our lines the customer wrote to, mirroring the inbox's "Para:" chip.
  // Inbound rows put the merchant connection in `toPhoneNumber`
  // (logic/whatsapp/inboundMessage.ts); outbound rows put the *customer's* number
  // there instead (merchantPortal/conversations.ts), so the chip is inbound-only —
  // on our own messages the recipient is already the whole conversation.
  const inboundLine = isIn ? message.toPhoneNumber : undefined;
  // A failed send kept the cheerful "sent" colour of its channel, so a message that never
  // arrived read exactly like a delivered one — the small warning glyph down on the
  // timestamp line was the only signal. The bubble itself now carries the failure.
  const isFailed = message.lastStatus?.toLowerCase() === "failed";

  return (
    <div className={cn("flex flex-col", isIn ? "items-start" : "items-end")}>
      {inboundLine && (
        <span
          className={cn(
            "mb-0.5 rounded-pill px-2 py-0.5 text-xs font-medium",
            // The secondary line is the exception worth noticing; the primary one
            // is the normal case and stays quiet.
            isSecondLine ? "bg-orange-100 text-orange-800" : "bg-blue-100 text-blue-800"
          )}
        >
          Para: {inboundLine}
        </span>
      )}
      {!isIn && (
        <span
          className={cn(
            "mb-0.5 rounded-pill px-2 py-0.5 text-xs font-medium",
            isFailed
              ? "bg-red-100 text-red-700"
              : isAI
                ? "bg-gray-100 text-gray-600"
                : "bg-[#DDDFE8] text-gray-700"
          )}
        >
          {isAI
            ? "Asistente IA"
            : isMerchantApi
              ? "API"
              : message.sourceId && message.sourceId !== "unknown"
                ? message.sourceId
                : "Asesor"}
        </span>
      )}
      <div
        className={cn(
          "max-w-[80%] whitespace-pre-wrap rounded-md px-3 py-2 text-sm text-gray-800 lg:max-w-[68ch] lg:text-[15px]",
          isFailed
            ? "border border-red-200 bg-red-50"
            : isIn
              ? isSecondLine
                ? "bg-[#FFF3E0]"
                : "bg-[#E3F2FD]"
              : isAI
                ? "bg-[#E8F5E9]"
                : "bg-[#EBECF0]",
          message.audio?.url && "min-w-[240px]"
        )}
      >
        {message.audio?.url ? (
          <div className="space-y-1">
            {/* The native <audio controls> widget renders as OS chrome — chunky, and
                different in every browser — which reads badly inside a bubble. The
                transcription stays open rather than sitting behind a toggle: this
                panel is for scanning a conversation, and reading a column of text
                beats playing each clip. */}
            <AudioPlayer audioUrl={message.audio.url} isCustomer={isIn} />
            {message.audio.transcription ? (
              <p className="text-xs italic text-gray-600">{message.audio.transcription}</p>
            ) : null}
          </div>
        ) : message.image?.url ? (
          <img
            src={message.image.url}
            alt="Imagen compartida"
            className="max-h-[400px] max-w-full rounded-md object-contain"
          />
        ) : message.video?.url ? (
          <video src={message.video.url} controls className="max-h-[400px] max-w-full rounded-md">
            Tu navegador no soporta el elemento de video.
          </video>
        ) : message.document?.url ? (
          <a
            href={message.document.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <FileText className="size-5 text-gray-600" />
            <span className="truncate text-sm">{message.content?.trim() || "Documento"}</span>
          </a>
        ) : (
          message.content
        )}
      </div>
      <span className="mt-0.5 inline-flex items-center text-xs text-gray-500">
        {formatTime(message.createdAt)}
        <MessageStatus message={message} onRetry={onRetry} />
      </span>
    </div>
  );
};

const NotaInterna = ({ item }: { item: NoteItem }) => {
  const { note } = item;

  return (
    <div className="mx-auto w-full max-w-[85%] rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2">
      <span className="flex flex-wrap items-center gap-1 text-xs text-neutral-600">
        <Lock className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="font-medium text-neutral-800">Nota interna</span>
        <span>·</span>
        <span>
          {noteAuthorLabel(note.authorName, note.authorEmail)} · {formatTime(note.createdAt)}
          {note.editedAt ? " · editada" : ""}
        </span>
      </span>
      <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-900 [overflow-wrap:anywhere]">
        {note.body}
      </p>
    </div>
  );
};

const ChatTimeline = ({
  items,
  isLoading,
  allPositions,
  onRetryMessage,
}: {
  items: TimelineItem[];
  isLoading: boolean;
  allPositions: PositionRef[];
  onRetryMessage?: (messageId: string) => void;
}) => {
  const positionNameById = new Map(allPositions.map((p) => [p.id, p.displayName]));

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <Spinner size="lg" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-center text-sm text-gray-500">No hay mensajes en esta conversación</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {items.map((item, index) => {
        switch (item.type) {
          case "message":
            return (
              <Burbuja
                key={`msg-${item.message.id ?? index}`}
                item={item}
                onRetry={
                  item.message.id &&
                  onRetryMessage &&
                  !item.message.isOptimistic &&
                  item.message.resendable
                    ? () => onRetryMessage(item.message.id!)
                    : undefined
                }
              />
            );
          case "activity":
            return (
              <div key={`activity-${item.activity.id}`} className="flex justify-center">
                <span className="rounded-pill bg-neutral-100 px-3 py-1 text-xs text-neutral-600">
                  {activityLabel(item.activity, positionNameById)} ·{" "}
                  {formatTime(item.activity.createdAt)}
                </span>
              </div>
            );
          case "note":
            return <NotaInterna key={`note-${item.note.id}`} item={item} />;
        }
      })}
    </div>
  );
};

export default ChatTimeline;
